import React, { ReactNode, useMemo } from "react";
import { Box } from "@mantine/core";
import { motion, AnimatePresence, Easing } from "framer-motion";
import { BlockTransitionAnimationType } from "@/components/features";

export interface ContentBlockTransitionProps {
  /** Current content to display */
  children: ReactNode;

  /** Unique key that changes when content should transition */
  transitionKey: string | number;

  /** Transition duration in milliseconds */
  duration?: number;

  /** Type of transition animation */
  type?: BlockTransitionAnimationType;

  /** Additional CSS class name for styling */
  className?: string;
}

export const ContentBlockTransition: React.FC<ContentBlockTransitionProps> = ({
  children,
  transitionKey,
  duration = 300,
  type = "fade",
  className,
}) => {
  // Convert duration from milliseconds to seconds for framer-motion
  const durationInSeconds = duration / 1000;

  // Animation variants for different transition types
  const getAnimationVariants = () => {
    switch (type) {
      case "fade":
        return {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        };

      case "slide-left":
        return {
          initial: { x: "100%" },
          animate: { x: 0 },
          exit: { x: "-100%" },
        };

      case "slide-right":
        return {
          initial: { x: "-100%" },
          animate: { x: 0 },
          exit: { x: "100%" },
        };

      case "slide-up":
        return {
          initial: { y: "100%" },
          animate: { y: 0 },
          exit: { y: "-100%" },
        };

      case "slide-down":
        return {
          initial: { y: "-100%" },
          animate: { y: 0 },
          exit: { y: "100%" },
        };

      case "scale":
        return {
          initial: { scale: 0.8, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 1.1, opacity: 0 },
        };

      case "scale-rotate":
        return {
          initial: { scale: 0.5, rotate: -180, opacity: 0 },
          animate: { scale: 1, rotate: 0, opacity: 1 },
          exit: { scale: 0.5, rotate: 180, opacity: 0 },
        };

      case "flip":
        return {
          initial: { rotateY: -90, opacity: 0 },
          animate: { rotateY: 0, opacity: 1 },
          exit: { rotateY: 90, opacity: 0 },
        };

      case "flip-x":
        return {
          initial: { rotateX: -90, opacity: 0 },
          animate: { rotateX: 0, opacity: 1 },
          exit: { rotateX: 90, opacity: 0 },
        };

      case "zoom-out":
        return {
          initial: { scale: 1.2, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 0.8, opacity: 0 },
        };

      case "push-left":
        return {
          initial: { x: "100%", scale: 0.9 },
          animate: { x: 0, scale: 1 },
          exit: { x: "-30%", scale: 0.9 },
        };

      case "push-right":
        return {
          initial: { x: "-100%", scale: 0.9 },
          animate: { x: 0, scale: 1 },
          exit: { x: "30%", scale: 0.9 },
        };

      case "curtain":
        return {
          initial: { scaleY: 0, transformOrigin: "top" },
          animate: { scaleY: 1, transformOrigin: "top" },
          exit: { scaleY: 0, transformOrigin: "bottom" },
        };

      case "blur":
        return {
          initial: { filter: "blur(10px)", opacity: 0, scale: 1.1 },
          animate: { filter: "blur(0px)", opacity: 1, scale: 1 },
          exit: { filter: "blur(10px)", opacity: 0, scale: 0.9 },
        };

      case "bounce":
        return {
          initial: { y: "100%", scale: 0.8 },
          animate: { y: 0, scale: 1 },
          exit: { y: "-100%", scale: 0.8 },
        };

      case "none":
      default:
        return {
          initial: {},
          animate: {},
          exit: {},
        };
    }
  };

  // Memoize so the objects keep a stable identity across re-renders. The parent
  // re-renders frequently (e.g. the progress-bar tick every 50ms); if these were
  // recreated each time, framer-motion would restart the in-flight transition on
  // every tick and its exit animation would never complete — leaving stale/stuck
  // blocks and stalling the rotation. See ContentBlockArea for the matching memo.
  const variants = useMemo(() => getAnimationVariants(), [type]);

  // Transition configuration
  const getEasing = (): Easing => {
    switch (type) {
      case "scale":
      case "scale-rotate":
      case "bounce":
        return [0.68, -0.55, 0.265, 1.55]; // backOut cubic-bezier for bouncy effects
      case "blur":
      case "curtain":
        return [0.25, 0.46, 0.45, 0.94]; // easeOutQuad for smooth effects
      case "zoom-out":
      case "push-left":
      case "push-right":
        return [0.23, 1, 0.32, 1]; // easeOutQuint for dramatic effects
      default:
        return [0.4, 0, 0.2, 1]; // easeInOut cubic-bezier for standard transitions
    }
  };

  const transition = useMemo(
    () => ({
      duration: durationInSeconds,
      ease: getEasing(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [durationInSeconds, type],
  );

  // For no transition, render directly without AnimatePresence
  if (type === "none") {
    return (
      <Box
        className={`content-block-transition ${className || ""}`}
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box style={{ width: "100%", height: "100%" }}>{children}</Box>
      </Box>
    );
  }

  return (
    <Box
      className={`content-block-transition content-block-transition--${type} ${
        className || ""
      }`}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={transitionKey}
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={transition}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transformOrigin: "center",
            // Ensure smooth rendering
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            // Hardware acceleration
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
        >
          <Box style={{ width: "100%", height: "100%" }}>{children}</Box>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};
